/**
 * Last line of defence for addresses hosting answers itself (payment links /p/**, /pay, shared
 * report links, static forms, webhooks).
 *
 * The app-shell worker denies those navigations (navigationDenylist.ts), but a device can still
 * be controlled by a worker built BEFORE that list existed (2026-10-17: the CEO's phone showed
 * the app's "Page not found" for a customer payment link). That old worker cannot be patched;
 * what it serves, though, is the app code fetched fresh from the network (navigations are
 * network-first). So this code runs, sees it was handed a server address, and:
 *   1. unregisters the worker, so the next navigation is not intercepted by it at all;
 *   2. reloads the same address once, with `location.replace` (no extra history entry).
 * The app re-registers the newest worker the next time it boots on an app address.
 * From NotFound (an in-app link reached the router) the worker is kept: the page's controller
 * is the one this code booted under and may be current; a plain full navigation is enough, and
 * if that worker turns out to be stale the boot check above runs on the reloaded page.
 *
 * Loop protection: one attempt per address per ESCAPE_WINDOW_MS, stamped in sessionStorage
 * (keyed by a digest of the address, never the address itself) BEFORE reloading. Nothing happens
 * while offline. With no usable sessionStorage it does nothing, because a loop could then
 * not be stopped; the person sees the normal "Page not found" screen instead.
 */
import serverHandledPaths from '../../generated/serverHandledPaths.json';
import { buildNavigationDenylist } from './navigationDenylist';

export const ESCAPE_WINDOW_MS = 60_000;
/** Most a hung getRegistrations()/unregister() may delay the reload. */
const UNREGISTER_CAP_MS = 2_000;
const STAMP_PREFIX = 'officehub:sw-escape:';

const denylist = buildNavigationDenylist(serverHandledPaths);

export interface EscapeEnv {
  location: Pick<Location, 'pathname' | 'search' | 'href' | 'replace'>;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  serviceWorker?: Pick<ServiceWorkerContainer, 'controller' | 'getRegistrations'>;
  now: () => number;
  /** navigator.onLine. Offline, a reload would trade the working cached app for an error page. */
  online?: boolean;
}

/** Same predicate the worker uses (pathname + search), from the same generated list. */
export function isServerHandledAddress(pathAndSearch: string): boolean {
  return denylist.some((re) => re.test(pathAndSearch));
}

function browserEnv(): EscapeEnv {
  let storage: EscapeEnv['storage'] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null; // the accessor itself throws when site data is blocked
  }
  return {
    location: window.location,
    storage,
    serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
    now: () => Date.now(),
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
  };
}

/**
 * Short non-reversible digest of the address for the sessionStorage stamp key. The path of a
 * payment link can carry its token, and nothing secret belongs in storage keys (FNV-1a, 32 bit:
 * only needs to tell this tab's few addresses apart).
 */
function addressDigest(pathname: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < pathname.length; i += 1) {
    h ^= pathname.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** Claim this address's single attempt. False when already tried recently or storage is unusable. */
function claimAttempt(env: EscapeEnv, key: string): boolean {
  if (!env.storage) return false;
  try {
    const last = Number(env.storage.getItem(key));
    if (last && env.now() - last < ESCAPE_WINDOW_MS) return false;
    env.storage.setItem(key, String(env.now()));
    return true;
  } catch {
    return false;
  }
}

async function unregisterAll(sw: EscapeEnv['serviceWorker']): Promise<void> {
  if (!sw?.controller) return;
  const work = sw
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .then(() => undefined)
    .catch(() => undefined);
  const cap = new Promise<void>((r) => {
    setTimeout(r, UNREGISTER_CAP_MS);
  });
  await Promise.race([work, cap]);
}

/**
 * If the current address belongs to the server, start handing it back to the network and return
 * true (the caller should not render the app). Otherwise return false.
 */
export function escapeToServerIfHandled(
  env: EscapeEnv = browserEnv(),
  { keepWorker = false }: { keepWorker?: boolean } = {},
): boolean {
  const { pathname, search, href } = env.location;
  if (!isServerHandledAddress(pathname + search)) return false;
  // Offline: leave the app running. Checked before the stamp so a visit that did nothing does not
  // use up the address's one attempt.
  if (env.online === false) return false;
  // Separate stamps: an in-app attempt that lands on a stale worker must still leave the boot
  // attempt (which drops that worker) its own one try.
  if (!claimAttempt(env, STAMP_PREFIX + (keepWorker ? 'in-app:' : 'boot:') + addressDigest(pathname))) return false;
  if (keepWorker) {
    env.location.replace(href);
    return true;
  }
  void unregisterAll(env.serviceWorker).finally(() => env.location.replace(href));
  return true;
}
